// SAGE-SPEED-2 (#2491): parse the SSE stream that POST /api/v1/sage/messages
// returns when called with `Accept: text/event-stream`. The requesting client
// reads its own ordered, lossless stream here instead of the drop-prone global
// SSE hub. See docs/features/sage-response-streaming.md.

export interface SageSSEEvent {
  /** SSE event name: 'meta' | 'delta' | 'tool' | 'done' | 'error' (default 'message'). */
  event: string
  /** Parsed JSON `data:` payload, or the raw string if it wasn't JSON. */
  data: unknown
}

/**
 * Parse a buffer of SSE text into complete events plus any trailing partial
 * frame. Frames are separated by a blank line (`\n\n`). Handles CRLF, comment
 * lines (`:` prefix), multi-line `data:` (joined with `\n`), and a single
 * optional space after the field colon per the SSE spec.
 *
 * Returns the parsed events and the leftover (incomplete) tail to prepend to
 * the next read.
 */
export function parseSSEBuffer(buf: string): { events: SageSSEEvent[]; rest: string } {
  const normalized = buf.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const events: SageSSEEvent[] = []

  // Everything up to the last frame boundary is complete; the remainder is a
  // partial frame still being streamed.
  const lastBoundary = normalized.lastIndexOf('\n\n')
  if (lastBoundary === -1) {
    return { events, rest: normalized }
  }
  const complete = normalized.slice(0, lastBoundary)
  const rest = normalized.slice(lastBoundary + 2)

  for (const rawFrame of complete.split('\n\n')) {
    if (!rawFrame.trim()) continue
    let eventName = 'message'
    const dataLines: string[] = []
    for (const line of rawFrame.split('\n')) {
      if (line === '' || line.startsWith(':')) continue // comment / heartbeat
      const colon = line.indexOf(':')
      const field = colon === -1 ? line : line.slice(0, colon)
      let value = colon === -1 ? '' : line.slice(colon + 1)
      if (value.startsWith(' ')) value = value.slice(1)
      if (field === 'event') eventName = value
      else if (field === 'data') dataLines.push(value)
    }
    if (dataLines.length === 0 && eventName === 'message') continue
    const dataStr = dataLines.join('\n')
    let data: unknown = dataStr
    if (dataStr) {
      try {
        data = JSON.parse(dataStr)
      } catch {
        data = dataStr
      }
    }
    events.push({ event: eventName, data })
  }
  return { events, rest }
}

/**
 * Read a fetch Response body as an SSE stream, invoking `onEvent` for each
 * complete frame in arrival order. Resolves when the stream ends.
 */
export async function readSageStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (e: SageSSEEvent) => void,
): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      const { events, rest } = parseSSEBuffer(buf)
      buf = rest
      for (const e of events) onEvent(e)
    }
    // Flush any trailing complete frame left without a final blank line.
    buf += decoder.decode()
    if (buf.trim()) {
      const { events } = parseSSEBuffer(buf + '\n\n')
      for (const e of events) onEvent(e)
    }
  } finally {
    reader.releaseLock()
  }
}
